import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import { toast } from "sonner";
import { 
  LuArrowLeft, LuPlus, LuPhone, LuTrash2, LuBot, LuStar, LuPencil,
  LuInfo
} from "react-icons/lu";
import { FaWhatsapp } from "react-icons/fa";
import { API } from "../App";

const PhoneAssignmentsPage = ({ auth }) => {
  const [assignments, setAssignments] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    phone_number: "",
    agent_id: "",
    description: "",
    is_default: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [assignmentsRes, agentsRes] = await Promise.all([
        fetch(`${API}/phone-assignments`, {
          headers: { Authorization: `Bearer ${auth.token}` }
        }),
        fetch(`${API}/agents`, {
          headers: { Authorization: `Bearer ${auth.token}` }
        })
      ]);

      if (assignmentsRes.ok) {
        setAssignments(await assignmentsRes.json());
      }
      if (agentsRes.ok) {
        setAgents(await agentsRes.json());
      }
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      phone_number: "",
      agent_id: "",
      description: "",
      is_default: false
    });
    setEditingId(null);
  };

  const handleOpenDialog = (assignment = null) => {
    if (assignment) {
      setFormData({
        phone_number: assignment.phone_number,
        agent_id: assignment.agent_id,
        description: assignment.description || "",
        is_default: assignment.is_default
      });
      setEditingId(assignment.id);
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = editingId 
        ? `${API}/phone-assignments/${editingId}`
        : `${API}/phone-assignments`;
      
      const response = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        if (editingId) {
          setAssignments(assignments.map(a => a.id === editingId ? result : a));
          toast.success("Asignación actualizada");
        } else {
          setAssignments([...assignments, result]);
          toast.success("Asignación creada");
        }
        setDialogOpen(false);
        resetForm();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al guardar");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta asignación?")) return;

    try {
      const response = await fetch(`${API}/phone-assignments/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.token}` }
      });

      if (response.ok) {
        setAssignments(assignments.filter(a => a.id !== id));
        toast.success("Asignación eliminada");
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleToggleActive = async (assignment) => {
    try {
      const response = await fetch(`${API}/phone-assignments/${assignment.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ is_active: !assignment.is_active })
      });

      if (response.ok) {
        const updated = await response.json();
        setAssignments(assignments.map(a => a.id === assignment.id ? updated : a));
        toast.success(updated.is_active ? "Activada" : "Desactivada");
      }
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" className="rounded-xl shrink-0" data-testid="back-btn">
                <LuArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center shrink-0">
                <LuPhone className="w-5 h-5 text-green-500" />
              </div>
              <div className="min-w-0">
                <h1 className="font-heading font-bold text-lg text-foreground truncate">Asignación de Números</h1>
                <p className="text-xs text-muted-foreground truncate">Configura qué agente atiende cada número</p>
              </div>
            </div>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="rounded-full bg-primary hover:bg-primary/90 glow-primary w-full sm:w-auto"
                onClick={() => handleOpenDialog()}
                data-testid="add-assignment-btn"
              >
                <LuPlus className="w-5 h-5 mr-2" />
                Nueva Asignación
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-foreground">
                  {editingId ? "Editar Asignación" : "Nueva Asignación"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-foreground">Número de Teléfono</Label>
                  <div className="relative">
                    <FaWhatsapp className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                    <Input
                      placeholder="5215512345678 o 521* para prefijo"
                      className="pl-10 bg-muted/50 border-transparent focus:border-primary rounded-xl"
                      value={formData.phone_number}
                      onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                      required
                      data-testid="phone-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <LuInfo className="w-3 h-3" />
                    Usa * al final para coincidir con prefijos (ej: 521* para México)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-foreground">Agente Asignado</Label>
                  <Select
                    value={formData.agent_id}
                    onValueChange={(value) => setFormData({ ...formData, agent_id: value })}
                  >
                    <SelectTrigger className="bg-muted/50 border-transparent rounded-xl" data-testid="agent-select">
                      <SelectValue placeholder="Selecciona un agente" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {agents.map((agent) => (
                        <SelectItem key={agent.id} value={agent.id}>
                          <div className="flex items-center gap-2">
                            <LuBot className="w-4 h-4 text-primary" />
                            {agent.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-foreground">Descripción (opcional)</Label>
                  <Input
                    placeholder="Ej: Clientes de México"
                    className="bg-muted/50 border-transparent focus:border-primary rounded-xl"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="description-input"
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30">
                  <div>
                    <p className="font-medium text-foreground">Agente por Defecto</p>
                    <p className="text-sm text-muted-foreground">
                      Atenderá números sin asignación específica
                    </p>
                  </div>
                  <Switch
                    checked={formData.is_default}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
                    data-testid="default-switch"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    className="rounded-full bg-primary" 
                    disabled={saving || !formData.agent_id}
                    data-testid="save-assignment-btn"
                  >
                    {saving ? "Guardando..." : editingId ? "Actualizar" : "Crear"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card className="glass-card border-white/10 border-primary/30">
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <LuInfo className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-foreground mb-1">¿Cómo funciona?</h3>
                  <p className="text-sm text-muted-foreground">
                    Cuando llega un mensaje de WhatsApp, BotNexus busca una asignación en este orden:
                    <br />
                    1. Coincidencia exacta del número → 2. Coincidencia por prefijo (521*) → 3. Agente por defecto → 4. Cualquier agente activo
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Assignments List */}
        {assignments.length === 0 ? (
          <Card className="glass-card border-white/10 border-dashed">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <LuPhone className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                Sin asignaciones
              </h3>
              <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                Crea asignaciones para dirigir mensajes de diferentes números a agentes específicos
              </p>
              <Button
                onClick={() => handleOpenDialog()}
                className="rounded-full bg-primary hover:bg-primary/90"
              >
                <LuPlus className="w-5 h-5 mr-2" />
                Crear Primera Asignación
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {assignments.map((assignment, index) => (
              <motion.div
                key={assignment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card 
                  className={`glass-card border-white/10 hover:border-primary/30 transition-all ${
                    !assignment.is_active ? "opacity-60" : ""
                  }`}
                  data-testid={`assignment-${assignment.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center shrink-0">
                          <FaWhatsapp className="w-6 h-6 text-green-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono font-semibold text-foreground">
                              {assignment.phone_number}
                            </span>
                            {assignment.is_default && (
                              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                                <LuStar className="w-3 h-3 mr-1" />
                                Default
                              </Badge>
                            )}
                            {!assignment.is_active && (
                              <Badge variant="secondary">Inactivo</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <LuBot className="w-4 h-4 text-primary" />
                            <span>{assignment.agent_name}</span>
                            {assignment.description && (
                              <>
                                <span>•</span>
                                <span>{assignment.description}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Switch
                          checked={assignment.is_active}
                          onCheckedChange={() => handleToggleActive(assignment)}
                          data-testid={`toggle-${assignment.id}`}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-primary"
                          onClick={() => handleOpenDialog(assignment)}
                          data-testid={`edit-${assignment.id}`}
                        >
                          <LuPencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => handleDelete(assignment.id)}
                          data-testid={`delete-${assignment.id}`}
                        >
                          <LuTrash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Quick Stats */}
        {assignments.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4"
          >
            <Card className="glass-card border-white/10">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-foreground">{assignments.length}</p>
                <p className="text-sm text-muted-foreground">Total Asignaciones</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-white/10">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-400">
                  {assignments.filter(a => a.is_active).length}
                </p>
                <p className="text-sm text-muted-foreground">Activas</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-white/10">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-400">
                  {assignments.filter(a => a.is_default).length}
                </p>
                <p className="text-sm text-muted-foreground">Por Defecto</p>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default PhoneAssignmentsPage;
